import type { IntakePlaybook, IntakePlaybookReady, IntakePlaybookUpdated } from '@merlin/project-schema/intake-playbook';

const offeringNames: Record<IntakePlaybook['offering']['id'], string> = {
  'growth-suite': 'The Growth Suite', 'the-forge': 'The Forge', 'bos-ai': 'BOS AI', undetermined: 'Explore the right fit',
};
const escape = (text: string): string => text.replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
})[character]!);

function planSections(plan: IntakePlaybook): string {
  return `<section><h3>The problem</h3><p>${escape(plan.problem)}</p></section>
    <section><h3>The outcome you want</h3><p>${escape(plan.desiredOutcome)}</p></section>
    <section><h3>Your next moves</h3><ol class="playbook-actions">${plan.actions.map(action => `<li><h4>${escape(action.title)}</h4><p>${escape(action.detail)}</p></li>`).join('')}</ol></section>
    <section class="playbook-fit"><h3>A possible fit · ${escape(offeringNames[plan.offering.id])}</h3><p>${escape(plan.offering.reason)}</p></section>
    ${plan.assumptions.length ? `<section><h3>Assumptions &amp; open questions</h3><ul>${plan.assumptions.map(assumption => `<li>${escape(assumption)}</li>`).join('')}</ul></section>` : ''}`;
}

/** No executable markup, remote resources, contact email/phone or server file. */
export function renderPlaybookDocument(value: IntakePlaybookReady): string {
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'">
<title>${escape(value.playbook.title)} · Merlin playbook</title>
<style>
:root{color-scheme:light;font-family:system-ui,sans-serif;color:#17213b;background:#eef2fa}
*{box-sizing:border-box}body{margin:0;padding:40px 20px;line-height:1.65}main{max-width:850px;margin:auto;background:white;padding:48px;border-top:6px solid #2d63c0;border-radius:12px;box-shadow:0 14px 48px #17213b12}
header{border-bottom:1px solid #dce3f0;padding-bottom:24px;margin-bottom:28px}.brand{color:#2d63c0;font-size:12px;font-weight:700;letter-spacing:.13em;text-transform:uppercase}h1{font-size:clamp(26px,5vw,38px);line-height:1.2;margin:18px 0}h3{font-size:14px;color:#2d63c0;margin:0 0 8px}h4{font-size:17px;margin:0 0 6px}p{margin:0 0 10px}section{margin:24px 0;break-inside:avoid}.playbook-actions{padding-left:26px}.playbook-actions li{padding:4px 0 16px 8px}.playbook-actions li::marker{font-weight:700;color:#2d63c0}.playbook-fit{background:#eef3ff;border-left:3px solid #2d63c0;padding:18px}footer{border-top:1px solid #dce3f0;margin-top:32px;padding-top:18px;font-size:12px;color:#53617a}h1,h3,h4,p,li{overflow-wrap:anywhere}ul{padding-left:22px}
@media(max-width:540px){body{padding:12px}main{padding:24px}}
@media print{@page{margin:18mm}body{padding:0;background:white}main{max-width:none;padding:0;border:0;border-radius:0;box-shadow:none}.playbook-fit{background:white}h1{font-size:28px}footer{font-size:10px}}
</style></head><body><main>
<header><div class="brand">Artificers AI · Merlin, Resident Artificer</div><h1>${escape(value.playbook.title)}</h1>
<p>Prepared for <strong>${escape(value.preparedFor.name)}</strong> · ${escape(value.preparedFor.organisation)}</p><p>Your conversation playbook</p></header>
${planSections(value.playbook)}
<footer>A starting point from your conversation with Merlin. Assumptions and possible fits need checking with your team; this is not an agreed scope or a delivery promise. Open this file in your browser to read or print it.</footer>
</main></body></html>`;
}

/** Session-only preview plus a download requiring both contact and document authority. */
export class IntakePlaybookCard {
  private current?: IntakePlaybookUpdated;
  private document?: IntakePlaybookReady;
  private contactRevision?: number;
  private confirmed = false;
  private objectUrl?: string;
  private staleUrl?: string;

  constructor(private readonly root: HTMLElement, private readonly onContact: () => void) {
    root.classList.add('intake-playbook');
    root.setAttribute('aria-label', 'Your live playbook');
    root.hidden = true;
  }

  update(value: IntakePlaybookUpdated): void {
    if (this.current && value.revision <= this.current.revision) return;
    this.revokeDocument();
    this.current = value;
    const activeAction = this.root.contains(window.document.activeElement)
      ? (window.document.activeElement as HTMLElement).dataset.action : undefined;
    this.root.innerHTML = `<header><p class="playbook-eyebrow">Your live playbook</p><h2>${escape(value.playbook.title)}</h2><p class="playbook-intro">A starting point we can shape together.</p></header>
      <div class="playbook-body">${planSections(value.playbook)}</div>
      <footer><p data-status role="status" aria-live="polite"></p><div class="playbook-controls">
      <button type="button" data-action="contact">Review details &amp; request follow-up</button>
      <a data-action="download" role="link" aria-disabled="true" tabindex="-1" download="merlin-playbook.html">Download playbook</a>
      </div><p class="playbook-format">HTML file · open in your browser to read or print. Download during this call.</p></footer>`;
    this.root.querySelector<HTMLButtonElement>('[data-action="contact"]')!.addEventListener('click', this.onContact);
    this.root.querySelector<HTMLAnchorElement>('[data-action="download"]')!.addEventListener('click', event => {
      if (!this.objectUrl) { event.preventDefault(); return; }
      this.status('Your browser handles saving. Open the HTML file to read or print your playbook.');
    });
    this.root.hidden = false;
    this.refresh();
    if (activeAction) this.root.querySelector<HTMLElement>(`[data-action="${activeAction}"]`)?.focus();
  }

  setContactRevision(revision: number): void {
    if (this.contactRevision === revision) return;
    this.invalidateContact();
    this.contactRevision = revision;
  }

  confirmContact(revision: number): void {
    if (this.contactRevision !== revision) return;
    this.confirmed = true;
    this.refresh();
  }

  invalidateContact(): void {
    this.confirmed = false;
    this.revokeDocument();
    this.refresh();
  }

  ready(value: IntakePlaybookReady): void {
    if (value.revision !== this.current?.revision || value.contactRevision !== this.contactRevision
      || JSON.stringify(value.playbook) !== JSON.stringify(this.current.playbook)) return;
    this.document = value;
    this.refresh();
  }

  clear(): void {
    this.revokeDocument();
    this.revokeStale();
    this.current = undefined;
    this.contactRevision = undefined;
    this.confirmed = false;
    this.root.innerHTML = '';
    this.root.hidden = true;
  }

  /**
   * Cuts off access to the current download immediately (the anchor's href
   * is removed on the next refresh) but does not revoke the Blob URL yet — a
   * save the visitor already started can still be reading it. The actual
   * `URL.revokeObjectURL` is deferred to the next document creation or to
   * `clear()` (see references/playbook-tools.md:20).
   */
  private revokeDocument(): void {
    if (this.objectUrl) {
      if (this.staleUrl) URL.revokeObjectURL(this.staleUrl);
      this.staleUrl = this.objectUrl;
    }
    this.objectUrl = undefined;
    this.document = undefined;
  }

  private revokeStale(): void {
    if (this.staleUrl) { URL.revokeObjectURL(this.staleUrl); this.staleUrl = undefined; }
  }

  private status(message: string): void {
    const status = this.root.querySelector<HTMLElement>('[data-status]');
    if (status) status.textContent = message;
  }

  private refresh(): void {
    const link = this.root.querySelector<HTMLAnchorElement>('[data-action="download"]');
    if (!link) return;
    if (this.confirmed && this.document && !this.objectUrl) {
      this.revokeStale();
      this.objectUrl = URL.createObjectURL(new Blob([renderPlaybookDocument(this.document)], { type: 'text/html;charset=utf-8' }));
    }
    if (this.objectUrl) link.href = this.objectUrl;
    else link.removeAttribute('href');
    link.setAttribute('aria-disabled', String(!this.objectUrl));
    link.tabIndex = this.objectUrl ? 0 : -1;
    this.status(this.objectUrl ? 'Your playbook is ready to download.' : this.confirmed
      ? 'Preparing this version of your playbook…'
      : 'Confirm your details and team follow-up consent to unlock the download.');
  }
}
